import { cn } from "@/lib/utils";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { PlusOptionsMenu } from "./PlusOptionsMenu";

interface MessageInputProps {
  onSend: (message: string) => void;
  isThinking?: boolean;
  placeholder?: string;
  onSummary?: () => void;
  isSummarizing?: boolean;
  onGenerateExercise?: () => void;
  onGiveHint?: () => void;
  onContinueSession?: () => void;
}

export function MessageInput({
  onSend,
  isThinking = false,
  placeholder = "Message Chicky Language Tutor...",
  onSummary,
  isSummarizing = false,
  onGenerateExercise,
  onGiveHint,
  onContinueSession,
}: MessageInputProps) {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && !isThinking) {
      onSend(inputValue.trim());
      setInputValue("");
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  useEffect(() => {
    if (!isThinking) {
      inputRef.current?.focus();
    }
  }, [isThinking]);

  return (
    <div
      className="border-t border-white/10 bg-[#0f0f14] px-3 pt-3 sm:px-5"
      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
    >
      <div className="mx-auto flex w-full max-w-4xl items-end gap-2">
        <PlusOptionsMenu
          onSummary={onSummary}
          isSummarizing={isSummarizing}
          onGenerateExercise={onGenerateExercise}
          onGiveHint={onGiveHint}
          onContinueSession={onContinueSession}
        />
        <form onSubmit={handleSubmit} className="flex min-w-0 flex-1 items-end gap-2">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
            placeholder={placeholder}
            className={cn(
              "h-11 min-w-0 flex-1 rounded-md border-white/10 bg-white/[0.045] px-4 text-[15px] text-white placeholder:text-white/30",
              "focus:border-orange-300/45 focus:ring-orange-300/20",
              "disabled:cursor-not-allowed disabled:opacity-50"
            )}
            disabled={isThinking}
          />
          <Button
            type="submit"
            disabled={isThinking || !inputValue.trim()}
            className={cn(
              "h-11 w-11 shrink-0 rounded-md bg-[#FD954F] text-[#1C1C1E] transition-colors",
              "hover:bg-[#F28747] disabled:bg-white/[0.06] disabled:text-white/25"
            )}
            title="Send message"
            aria-label="Send message"
          >
            <Send size={18} />
          </Button>
        </form>
      </div>
      <p className="mt-2 text-center text-[11px] text-white/30">
        Chicky can make mistakes. Check important info.
      </p>
    </div>
  );
}
